import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { Link } from "react-router-dom";
import "./Register.css";
import Select from "react-select";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  getUndergraduateSchoolProgramMap,
  getPostgraduateSchoolProgramMap,
} from "../../utils/programOptions";

const schoolProgramMapBasicEducation = {
  "Grade School": [
    "Kindergarten",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
  ],
  "Junior High School": ["Grade 7", "Grade 8", "Grade 9", "Grade 10"],
  "Senior High School": ["Grade 11", "Grade 12"],
  "High School": ["High School", "Science High School"],
};

const Register = () => {
  const [formData, setFormData] = useState({
    // Step 1: Account Information
    email: "",
    password: "",
    confirmPassword: "",

    // Step 2: Personal Information
    idNo: "",
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    birthday: "",

    // Father's Information
    fatherFirstName: "",
    fatherMiddleName: "",
    fatherLastName: "",
    fatherSuffix: "",

    // Mother's Information
    motherFirstName: "",
    motherMiddleName: "",
    motherLastName: "",
    motherSuffix: "",

    // Step 3: Educational Information
    schoolUndergraduate: "",
    programUndergraduate: "",
    schoolPostgraduate: "",
    programPostgraduate: "",
    recentGraduate: "",
    elementarySMU: "",
    juniorHighSMU: "",
    highSchoolSMU: "",
    hsOrShsSMU: "",
    seniorHighSMU: "",
    strandInSMU: "",
    tertiarySMU: "",
    nonGraduateSMU: "",
    achievements: ["", "", "", "", ""],

    // Step 4: Employment & Contact Information
    employmentStatus: "",
    otherEmploymentStatus: "",
    currentWork: "",
    companyName: "",
    companyAddress: "",
    address: "",
    facebookAccount: "",
    contactNumber: "",

    // Files (handled as File objects)
    curriculumVitae: null,
    photo: null,
    signature: null,
    alumniIdApplication: null,
  });
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [cvFileName, setCvFileName] = useState("");
  const [errors, setErrors] = useState({});
  const schoolProgramMapUndergraduate =
    getUndergraduateSchoolProgramMap(formData.tertiarySMU) || {};
  const schoolProgramMapPostgraduate =
    getPostgraduateSchoolProgramMap(formData.tertiarySMU) || {};
  const sortedSchoolProgramMap = Object.fromEntries(
    Object.entries(schoolProgramMapUndergraduate).sort((a, b) =>
      a[0].localeCompare(b[0]),
    ),
  );
  const availableUndergraduatePrograms =
    schoolProgramMapUndergraduate[formData.schoolUndergraduate] || [];
  const availablePostgraduatePrograms =
    schoolProgramMapPostgraduate[formData.schoolPostgraduate] || [];
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const fieldRefs = {
    email: useRef(null),
    password: useRef(null),
    confirmPassword: useRef(null),
    firstName: useRef(null),
    lastName: useRef(null),
    birthday: useRef(null),
    address: useRef(null),
    fatherFirstName: useRef(null),
    fatherLastName: useRef(null),
    motherFirstName: useRef(null),
    motherLastName: useRef(null),
    schoolUndergraduate: useRef(null),
    programUndergraduate: useRef(null),
    schoolPostgraduate: useRef(null),
    programPostgraduate: useRef(null),
    recentGraduate: useRef(null),
    elementarySMU: useRef(null),
    juniorHighSMU: useRef(null),
    highSchoolSMU: useRef(null),
    hsOrShsSMU: useRef(null),
    seniorHighSMU: useRef(null),
    strandInSMU: useRef(null),
    tertiarySMU: useRef(null),
    nonGraduateSMU: useRef(null),
    employmentStatus: useRef(null),
    currentWork: useRef(null),
    companyName: useRef(null),
    companyAddress: useRef(null),
    facebookAccount: useRef(null),
    contactNumber: useRef(null),
    curriculumVitae: useRef(null),
    photo: useRef(null),
  };
  const [consentStep1, setConsentStep1] = useState(false);

  // OTP
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpFeedback, setOtpFeedback] = useState("");
  const [otpDisabled, setOtpDisabled] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  useEffect(() => {
    let timer;

    if (otpDisabled && otpTimer > 0) {
      timer = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setOtpDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [otpDisabled, otpTimer]);

  const handleVerifyOtp = async () => {
    try {
      const res = await axios.post("/api/verify/check-otp", {
        email: formData.email,
        otp,
      });

      if (res.data.verified) {
        setOtpVerified(true);
        setOtpFeedback("OTP verified successfully!");
      } else {
        setOtpVerified(false);
        setOtpFeedback("Invalid or expired OTP.");
      }
    } catch (error) {
      setOtpVerified(false);
      setOtpFeedback("Verification failed. Try again.");
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email first" }));
      return;
    }

    try {
      await axios.post("/api/verify/send-otp", { email: formData.email });
      setOtpSent(true);
      setOtpFeedback("OTP sent! Check your email.");

      // Disable button and start 2-min countdown
      setOtpDisabled(true);
      setOtpTimer(120); // 120 seconds
    } catch (error) {
      setOtpFeedback("Failed to send OTP. Try again.");
    }
  };

  const scrollToFirstError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];
    if (fieldRefs[firstErrorKey] && fieldRefs[firstErrorKey].current) {
      fieldRefs[firstErrorKey].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      fieldRefs[firstErrorKey].current.focus();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const undergraduateProgramOptions = availableUndergraduatePrograms.map(
    (program) => ({
      label: program,
      value: program,
    }),
  );

  const postgraduateProgramOptions = availablePostgraduatePrograms.map(
    (program) => ({
      label: program,
      value: program,
    }),
  );

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (name === "contactNumber") {
      // Only allow digits
      if (!/^\d*$/.test(value)) return;
    }

    if (type === "file") {
      const file = files[0];

      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));

      if (file && name === "photo") {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
      }

      if (file && name === "curriculumVitae") {
        setCvFileName(file.name);
      }
    } else if (name === "tertiarySMU") {
      // Program lists depend on graduation era, so clear stale selections
      // whenever the graduation year changes.
      setFormData((prev) => ({
        ...prev,
        tertiarySMU: value,
        schoolUndergraduate: "",
        programUndergraduate: "",
        schoolPostgraduate: "",
        programPostgraduate: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear related errors when typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    // ✅ Require consent checkbox
    if (!consentStep1) {
      newErrors.consentStep1 =
        "You must acknowledge the data usage before continuing";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.birthday) newErrors.birthday = "Birthday is required";
    // if (!formData.idNo) newErrors.idNo = 'ID Number is required';
    // if (!formData.address) {
    //   newErrors.address = 'Address is required';
    // }

    if (!formData.fatherFirstName)
      newErrors.fatherFirstName = "Father’s first name is required";
    if (!formData.fatherLastName)
      newErrors.fatherLastName = "Father’s last name is required";

    if (!formData.motherFirstName)
      newErrors.motherFirstName = "Mother’s first name is required";
    if (!formData.motherLastName)
      newErrors.motherLastName = "Mother’s last name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const errors = {};

    if (!formData.schoolUndergraduate) {
      errors.schoolUndergraduate = "Undergraduate School is required.";
    }

    if (!formData.programUndergraduate.trim()) {
      errors.programUndergraduate = "Undergraduate Course/Program is required.";
    }

    if (!formData.recentGraduate) {
      errors.recentGraduate =
        "Please select if you graduated within the past five years.";
    }

    if (!formData.elementarySMU.trim()) {
      errors.elementarySMU =
        "Please indicate if you attended Grade School at SMU.";
    }

    if (!formData.juniorHighSMU.trim()) {
      errors.juniorHighSMU =
        "Please indicate if you attended Junior High at SMU.";
    }

    if (!formData.highSchoolSMU.trim()) {
      errors.highSchoolSMU =
        "Please indicate if you attended High School at SMU.";
    }

    if (!formData.seniorHighSMU.trim()) {
      errors.seniorHighSMU =
        "Please indicate if you attended Senior High at SMU.";
    }

    if (!formData.tertiarySMU.trim()) {
      errors.tertiarySMU = "Please indicate if you attended Tertiary at SMU.";
    }

    if (!formData.nonGraduateSMU.trim()) {
      errors.nonGraduateSMU =
        "Please indicate if you studied at SMU but did not graduate.";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep4 = (data) => {
    const errors = {};

    if (data.employmentStatus.length === 0) {
      errors.employmentStatus = "Please select at least one employment status";
    }

    if (
      data.employmentStatus.includes("Other") &&
      !data.otherEmploymentStatus
    ) {
      errors.otherEmploymentStatus = "Please specify your employment status";
    }

    if (!data.currentWork) {
      errors.currentWork = "Occupation is required";
    }

    if (!data.companyName) {
      errors.companyName = "Company name is required";
    }

    if (!data.companyAddress) {
      errors.companyAddress = "Company address is required";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    let isValid = false;

    if (step === 1) isValid = validateStep1();
    else if (step === 2) isValid = validateStep2();
    else if (step === 3) isValid = validateStep3();
    else isValid = validateStep4();

    if (!isValid) {
      scrollToFirstError(errors);
      return;
    }

    if (step < 4) {
      setStep((prevStep) => prevStep + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});

    if (
      !validateStep1() ||
      !validateStep2() ||
      !validateStep3() ||
      !validateStep4(formData)
    ) {
      scrollToFirstError(errors);
      return;
    }

    const formDataToSend = new FormData();

    // Step 1: Account Information
    formDataToSend.append("email", formData.email);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("confirmPassword", formData.confirmPassword);

    // Step 2: Personal Information
    formDataToSend.append("idNo", formData.idNo);
    formDataToSend.append("firstName", formData.firstName);
    formDataToSend.append("middleName", formData.middleName);
    formDataToSend.append("lastName", formData.lastName);
    formDataToSend.append("suffix", formData.suffix);
    formDataToSend.append("birthday", formData.birthday);

    // Father's Information
    formDataToSend.append("fatherFirstName", formData.fatherFirstName);
    formDataToSend.append("fatherMiddleName", formData.fatherMiddleName);
    formDataToSend.append("fatherLastName", formData.fatherLastName);
    formDataToSend.append("fatherSuffix", formData.fatherSuffix);

    // Mother's Information
    formDataToSend.append("motherFirstName", formData.motherFirstName);
    formDataToSend.append("motherMiddleName", formData.motherMiddleName);
    formDataToSend.append("motherLastName", formData.motherLastName);
    formDataToSend.append("motherSuffix", formData.motherSuffix);

    // Step 3: Educational Information
    formDataToSend.append("schoolUndergraduate", formData.schoolUndergraduate);
    formDataToSend.append(
      "programUndergraduate",
      formData.programUndergraduate,
    );
    formDataToSend.append("schoolPostgraduate", formData.schoolPostgraduate);
    formDataToSend.append("programPostgraduate", formData.programPostgraduate);
    formDataToSend.append("recentGraduate", formData.recentGraduate);
    formDataToSend.append("elementarySMU", formData.elementarySMU);
    formDataToSend.append("juniorHighSMU", formData.juniorHighSMU);
    formDataToSend.append("highSchoolSMU", formData.highSchoolSMU);
    formDataToSend.append("hsOrShsSMU", formData.hsOrShsSMU);
    formDataToSend.append("seniorHighSMU", formData.seniorHighSMU);
    formDataToSend.append("strandInSMU", formData.strandInSMU);
    formDataToSend.append("tertiarySMU", formData.tertiarySMU);
    formDataToSend.append("nonGraduateSMU", formData.nonGraduateSMU);
    formDataToSend.append(
      "achievements",
      JSON.stringify(formData.achievements),
    );

    formDataToSend.append("employmentStatus", formData.employmentStatus);
    formDataToSend.append("currentWork", formData.currentWork);
    formDataToSend.append("companyName", formData.companyName);
    formDataToSend.append("companyAddress", formData.companyAddress);
    formDataToSend.append("address", formData.address);
    formDataToSend.append("facebookAccount", formData.facebookAccount);
    formDataToSend.append("contactNumber", formData.contactNumber);

    // Files
    if (formData.curriculumVitae) {
      formDataToSend.append("curriculumVitae", formData.curriculumVitae);
    }

    if (formData.photo) {
      formDataToSend.append("photo", formData.photo);
    }

    try {
      await axios.post("/api/auth/signup", formDataToSend);

      setSnackbarMessage("Registration successful. Awaiting admin approval.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const renderStep1 = () => {
    return (
      <>
        <h2 className="step-title">Step 1: Account Information</h2>
        <div className="form-field">
          <h3 className="consent-title" style={{ textAlign: "" }}>
            Data Privacy Consent
          </h3>
          <p className="consent-description" style={{ textAlign: "justify" }}>
            In accordance with the Data Privacy Act of 2012 (RA 10173), the
            Alumni Affairs Office (AAO) collects and processes the information
            provided in this form for legitimate institutional purposes such as
            alumni database management, tracer studies, alumni engagement,
            recognition, reporting, and accreditation. All personal data shall
            be handled securely and accessed only by authorized personnel.
          </p>
          <br />
          <div className="form-field">
            <label
              style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}
            >
              <input
                ref={fieldRefs.consentStep1}
                type="checkbox"
                checked={consentStep1}
                onChange={(e) => setConsentStep1(e.target.checked)}
                style={{ marginTop: "4px" }}
              />
              <span>
                I have read and understood this notice and voluntarily consent
                to the collection, processing, and use of my personal data for
                the purposes stated above.
              </span>
            </label>
            {errors.consentStep1 && (
              <p className="error-message">{errors.consentStep1}</p>
            )}
          </div>
          <br />
          <label>
            Email <span className="required">*</span>
          </label>
          <div style={{ display: "flex", gap: "10px" }}>
            <input
              ref={fieldRefs.email}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`input-field ${errors.email ? "input-error" : ""}`}
              placeholder="Enter email"
              required
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={otpDisabled}
              className="send-otp-button"
            >
              {otpDisabled
                ? `Wait ${Math.floor(otpTimer / 60)}:${(otpTimer % 60)
                    .toString()
                    .padStart(2, "0")}`
                : "Send OTP"}
            </button>
          </div>
          {errors.email && <p className="error-message">{errors.email}</p>}
        </div>

        {otpSent && (
          <div className="form-field">
            <label>Enter OTP sent to your email</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code"
              className="input-field"
            />
            <button
              type="button"
              className="verify-otp-button"
              onClick={handleVerifyOtp}
            >
              Verify OTP
            </button>
            <p className={otpVerified ? "otp-success" : "otp-error"}>
              {otpFeedback}
            </p>
          </div>
        )}

        {/* Password Field */}
        <div className="form-field">
          <label>
            Password <span className="required">*</span>
          </label>
          <div className="password-field">
            <input
              ref={fieldRefs.password}
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-control ${errors.password ? "input-error" : ""}`}
              placeholder="At least 8 characters"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="error-message">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="form-field">
          <label>
            Confirm Password <span className="required">*</span>
          </label>
          <div className="password-field">
            <input
              ref={fieldRefs.confirmPassword}
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`form-control ${
                errors.confirmPassword ? "input-error" : ""
              }`}
              placeholder="Confirm password"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
            >
              {showConfirmPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="error-message">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="buttons-container">
          <button
            type="button"
            className="next-button"
            onClick={nextStep}
            disabled={!otpVerified || !consentStep1}
            style={{
              opacity: otpVerified && consentStep1 ? 1 : 0.6,
              cursor: otpVerified && consentStep1 ? "pointer" : "not-allowed",
            }}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  // Render the second step with personal information
  const renderStep2 = () => {
    return (
      <>
        <h2 className="step-title">Step 2: Personal Information</h2>
        <div className="form-grid">
          <div className="form-field">
            <label>
              First Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.firstName}
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter first name"
              required
            />
            {errors.firstName && (
              <p className="error-message">{errors.firstName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Middle Name</label>
            <input
              type="text"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter middle name"
            />
          </div>

          <div className="form-field">
            <label>
              Last Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.lastName}
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter Last name"
              required
            />
            {errors.lastName && (
              <p className="error-message">{errors.lastName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Suffix </label>
            <input
              type="text"
              name="suffix"
              value={formData.suffix}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter suffix (if applicable)"
            />
          </div>

          <div className="form-field">
            <label>
              Birthday <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.birthday}
              type="date"
              name="birthday"
              value={formData.birthday}
              onChange={handleChange}
              className="input-field"
              required
            />
            {errors.birthday && (
              <p className="error-message">{errors.birthday}</p>
            )}
          </div>

          <div className="form-field">
            <label>ID Number {/*<span className="required">*</span> */}</label>
            <input
              type="text"
              name="idNo"
              value={formData.idNo}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter ID number"
            />
            {errors.idNo && <p className="error-message">{errors.idNo}</p>}
          </div>

          <div className="form-field">
            <label>Address {/*<span className="required">*</span> */}</label>
            <input
              ref={fieldRefs.address}
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="input-field"
              placeholder="Town and Province/State and Country"
            />
            {errors.address && (
              <p className="error-message">{errors.address}</p>
            )}
          </div>
        </div>

        <div className="left-aligned">
          <h4 className="subsection-title">Father's Information</h4>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>
              First Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.fatherFirstName}
              type="text"
              name="fatherFirstName"
              value={formData.fatherFirstName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter father's first name"
              required
            />
            {errors.fatherFirstName && (
              <p className="error-message">{errors.fatherFirstName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Middle Name</label>
            <input
              type="text"
              name="fatherMiddleName"
              value={formData.fatherMiddleName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter father's middle name"
            />
          </div>

          <div className="form-field">
            <label>
              Last Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.fatherLastName}
              type="text"
              name="fatherLastName"
              value={formData.fatherLastName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter father's last name"
              required
            />
            {errors.fatherLastName && (
              <p className="error-message">{errors.fatherLastName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Suffix</label>
            <input
              type="text"
              name="fatherSuffix"
              value={formData.fatherSuffix}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter father's suffix (if applicable)"
            />
          </div>
        </div>

        <div className="left-aligned">
          <h4 className="subsection-title">Mother's Maiden Information</h4>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>
              First Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.motherFirstName}
              type="text"
              name="motherFirstName"
              value={formData.motherFirstName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter mother's first name"
              required
            />
            {errors.motherFirstName && (
              <p className="error-message">{errors.motherFirstName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Maiden Middle Name</label>
            <input
              type="text"
              name="motherMiddleName"
              value={formData.motherMiddleName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter mother's maiden middle name"
            />
          </div>

          <div className="form-field">
            <label>
              Maiden Last Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.motherLastName}
              type="text"
              name="motherLastName"
              value={formData.motherLastName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter mother's maiden last name"
              required
            />
            {errors.motherLastName && (
              <p className="error-message">{errors.motherLastName}</p>
            )}
          </div>

          <div className="form-field">
            <label>Suffix</label>
            <input
              type="text"
              name="motherSuffix"
              value={formData.motherSuffix}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter mother's suffix (if applicable)"
            />
          </div>
        </div>

        <div className="buttons-container">
          <button type="button" className="prev-button" onClick={prevStep}>
            Previous
          </button>
          <button type="button" className="next-button" onClick={nextStep}>
            Next
          </button>
        </div>
      </>
    );
  };

  const renderStep3 = () => {
    return (
      <>
        <h2 className="step-title">Step 3: Education Information</h2>

        <div className="form-grid">
          <div className="form-field full-width">
            <label>
              Did you take your tertiary education in SMU?{" "}
              <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.tertiarySMU}
              type="text"
              name="tertiarySMU"
              value={formData.tertiarySMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State year of graduation. If NO, enter N/A"
              required
            />
            <small>
              Your year of graduation determines which schools/programs are
              available below.
            </small>
            {errors.tertiarySMU && (
              <p className="error-message">{errors.tertiarySMU}</p>
            )}
          </div>
        </div>

        <div className="left-aligned">
          <h4 className="subsection-title">Undergraduate Program</h4>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>
              School/College
              <span className="required">*</span>
            </label>
            <select
              ref={fieldRefs.schoolUndergraduate}
              name="schoolUndergraduate"
              value={formData.schoolUndergraduate}
              onChange={handleChange}
              className="input-field"
              disabled={Object.keys(sortedSchoolProgramMap).length === 0}
              required
            >
              <option value="" disabled>
                {Object.keys(sortedSchoolProgramMap).length === 0
                  ? "Enter your year of graduation above first"
                  : "Select School/College"}
              </option>
              {Object.keys(sortedSchoolProgramMap).map((school, index) => (
                <option key={index} value={school}>
                  {school}
                </option>
              ))}
            </select>
            {errors.schoolUndergraduate && (
              <p className="error-message">{errors.schoolUndergraduate}</p>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="programUndergraduate">
              Grade Level or Course/Program <span className="required">*</span>
            </label>
            <Select
              ref={fieldRefs.programUndergraduate}
              id="programUndergraduate"
              options={undergraduateProgramOptions}
              value={
                undergraduateProgramOptions.find(
                  (opt) => opt.value === formData.programUndergraduate,
                ) || null
              }
              onChange={(selectedOption) =>
                handleChange({
                  target: {
                    name: "programUndergraduate",
                    value: selectedOption?.value || "",
                  },
                })
              }
              isDisabled={undergraduateProgramOptions.length === 0}
              className="input-field"
              placeholder="Select a course/program"
              required
            />
            {errors.programUndergraduate && (
              <p className="error-message">{errors.programUndergraduate}</p>
            )}
          </div>
        </div>

        <div className="left-aligned">
          <h4 className="subsection-title">Postgraduate Program (Optional)</h4>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>School/College</label>
            <select
              ref={fieldRefs.schoolPostgraduate}
              name="schoolPostgraduate"
              value={formData.schoolPostgraduate}
              onChange={handleChange}
              className="input-field"
            >
              <option value="">Select School/College (Optional)</option>
              {Object.keys(schoolProgramMapPostgraduate).map(
                (school, index) => (
                  <option key={index} value={school}>
                    {school}
                  </option>
                ),
              )}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="programPostgraduate">
              Grade Level or Course/Program
            </label>
            <Select
              ref={fieldRefs.programPostgraduate}
              id="programPostgraduate"
              options={postgraduateProgramOptions}
              value={
                postgraduateProgramOptions.find(
                  (opt) => opt.value === formData.programPostgraduate,
                ) || null
              }
              onChange={(selectedOption) =>
                handleChange({
                  target: {
                    name: "programPostgraduate",
                    value: selectedOption?.value || "",
                  },
                })
              }
              isDisabled={postgraduateProgramOptions.length === 0}
              className="input-field"
              placeholder="Select a course/program"
            />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-field">
            <label>
              Are you a graduate for the past five years?{" "}
              <span className="required">*</span>
            </label>
            <select
              ref={fieldRefs.recentGraduate}
              name="recentGraduate"
              value={formData.recentGraduate}
              onChange={handleChange}
              className="select-field"
              required
            >
              <option value="">Select an option</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {errors.recentGraduate && (
              <p className="error-message">{errors.recentGraduate}</p>
            )}
          </div>
          <div></div>
          <div className="form-field full-width">
            <label>
              Did you take your Grade School in SMU?{" "}
              <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.elementarySMU}
              type="text"
              name="elementarySMU"
              value={formData.elementarySMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State year of graduation. If NO, enter N/A"
              required
            />
            {errors.elementarySMU && (
              <p className="error-message">{errors.elementarySMU}</p>
            )}
          </div>

          <div className="form-field full-width">
            <label>
              Did you take your Junior High in SMU?{" "}
              <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.juniorHighSMU}
              type="text"
              name="juniorHighSMU"
              value={formData.juniorHighSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State year of graduation. If NO, enter N/A"
              required
            />
            {errors.juniorHighSMU && (
              <p className="error-message">{errors.juniorHighSMU}</p>
            )}
          </div>

          <div className="form-field full-width">
            <label>
              Did you take your High School in SMU?{" "}
              <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.highSchoolSMU}
              type="text"
              name="highSchoolSMU"
              value={formData.highSchoolSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State year of graduation. If NO, enter N/A"
              required
            />

            <input
              ref={fieldRefs.hsOrShsSMU}
              type="text"
              name="hsOrShsSMU"
              value={formData.hsOrShsSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, Enter HS or Science HS"
            />
            {errors.highSchoolSMU && (
              <p className="error-message">{errors.highSchoolSMU}</p>
            )}
          </div>

          <div className="form-field full-width">
            <label>
              Did you take your Senior High School in SMU?{" "}
              <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.seniorHighSMU}
              type="text"
              name="seniorHighSMU"
              value={formData.seniorHighSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State year of graduation. If NO, enter N/A"
              required
            />
            <input
              type="text"
              name="strandInSMU"
              value={formData.strandInSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES: What Strand?"
              // required
            />
            {errors.seniorHighSMU && (
              <p className="error-message">{errors.seniorHighSMU}</p>
            )}
          </div>

          <div className="form-field full-width">
            <label>
              Did you study at SMU at any level but did not graduate/have a
              graduation ceremony? <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.nonGraduateSMU}
              type="text"
              name="nonGraduateSMU"
              value={formData.nonGraduateSMU}
              onChange={handleChange}
              className="input-field"
              placeholder="If YES, State your last year level. If NO, enter N/A"
              required
            />
            {errors.nonGraduateSMU && (
              <p className="error-message">{errors.nonGraduateSMU}</p>
            )}
          </div>

          <div className="form-field full-width">
            <label>Top 5 achievements during your stay in SMU</label>
            {formData.achievements.map((achievement, index) => (
              <textarea
                ref={fieldRefs.nonGraduateSMU}
                key={index}
                name={`achievement-${index}`}
                value={achievement}
                onChange={(e) => {
                  const updated = [...formData.achievements];
                  updated[index] = e.target.value;
                  setFormData({ ...formData, achievements: updated });
                }}
                className="input-field mt-2"
                placeholder={`Achievement ${index + 1}`}
                rows={2}
                // required
              />
            ))}
          </div>
        </div>

        <div className="buttons-container">
          <button type="button" className="prev-button" onClick={prevStep}>
            Previous
          </button>
          <button type="button" className="next-button" onClick={nextStep}>
            Next
          </button>
        </div>
      </>
    );
  };

  const renderStep4 = () => {
    return (
      <>
        <h2 className="step-title">Step 4: Employment & Contact Information</h2>
        <div className="form-grid">
          {/* Employment Status Section */}
          <div className="form-field full-width">
            <label>
              Employment Status <span className="required">*</span>
            </label>
            <select
              ref={fieldRefs.employmentStatus}
              name="employmentStatus"
              value={formData.employmentStatus}
              onChange={handleChange}
              className="input-field"
              required
            >
              {errors.employmentStatus && (
                <p className="error-message">{errors.employmentStatus}</p>
              )}
              <option value="" disabled>
                Select employment status
              </option>
              <option value="Government Employee">Government Employee</option>
              <option value="Private Employee">Private Employee</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="N/A">N/A</option>
              <option value="Other">Other</option>
            </select>

            {formData.employmentStatus === "Other" && (
              <div className="form-field mt-2">
                <input
                  type="text"
                  name="otherEmploymentStatus"
                  value={formData.otherEmploymentStatus}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Please specify your employment status"
                  // required
                />
              </div>
            )}
          </div>

          {/* Current Work */}
          <div className="form-field">
            <label>
              Occupation <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.currentWork}
              type="text"
              name="currentWork"
              value={formData.currentWork}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter occupation"
              disabled={formData.employmentStatus === "N/A"}
            />
            {errors.currentWork && (
              <p className="error-message">{errors.currentWork}</p>
            )}
          </div>

          {/* Company Name */}
          <div className="form-field">
            <label>
              Company Name <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.companyName}
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter company name"
              disabled={formData.employmentStatus === "N/A"}
            />
            {errors.companyName && (
              <p className="error-message">{errors.companyName}</p>
            )}
          </div>

          {/* Company Address */}
          <div className="form-field">
            <label>
              Company Address <span className="required">*</span>
            </label>
            <input
              ref={fieldRefs.companyAddress}
              type="text"
              name="companyAddress"
              value={formData.companyAddress}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter company address"
              disabled={formData.employmentStatus === "N/A"}
              required
            />
            {errors.companyAddress && (
              <p className="error-message">{errors.companyAddress}</p>
            )}
          </div>
          <div className="form-field"></div>

          {/* Facebook Account */}
          <div className="form-field">
            <label>Facebook Account</label>
            <input
              type="text"
              name="facebookAccount"
              value={formData.facebookAccount}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter Facebook account"
              // required
            />
          </div>

          {/* Contact Number */}
          <div className="form-field">
            <label>
              Contact Number {/*<span className="required">*</span> */}
            </label>
            <input
              ref={fieldRefs.contactNumber}
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]*"
              className="input-field"
              placeholder="Enter contact number"
              // required
            />

            {errors.contactNumber && (
              <p className="error-message">{errors.contactNumber}</p>
            )}
          </div>

          {/* File Upload for Photo */}
          <div className="form-field">
            <label>Photo</label>

            <div className="file-upload">
              <p>Drop your photo here to upload (Optional)</p>
              <input
                ref={fieldRefs.photo}
                type="file"
                name="photo"
                accept="image/*"
                onChange={handleChange}
              />
              {photoPreview && (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Profile preview" />
                </div>
              )}
              {errors.photo && <p className="error-message">{errors.photo}</p>}
            </div>

            {/* Put the clear button OUTSIDE the .file-upload box */}
            {formData.photo && (
              <div className="file-actions">
                <button
                  type="button"
                  className="clear-file-btn"
                  onClick={() => {
                    setFormData((p) => ({ ...p, photo: null }));
                    setPhotoPreview(null);
                    if (fieldRefs.photo.current)
                      fieldRefs.photo.current.value = "";
                  }}
                >
                  Clear Photo
                </button>
              </div>
            )}
          </div>

          {/* CV */}
          <div className="form-field">
            <label>Curriculum Vitae</label>

            <div className="file-upload">
              <p>Drop your CV file here to upload (Optional)</p>
              <input
                ref={fieldRefs.curriculumVitae}
                type="file"
                name="curriculumVitae"
                accept=".pdf,.doc,.docx"
                onChange={handleChange}
              />
              {cvFileName && (
                <div className="file-name">Selected file: {cvFileName}</div>
              )}
            </div>

            {formData.curriculumVitae && (
              <div className="file-actions">
                <button
                  type="button"
                  className="clear-file-btn"
                  onClick={() => {
                    setFormData((p) => ({ ...p, curriculumVitae: null }));
                    setCvFileName("");
                    if (fieldRefs.curriculumVitae.current)
                      fieldRefs.curriculumVitae.current.value = "";
                  }}
                >
                  Clear CV
                </button>
              </div>
            )}
          </div>

          <div className="form-field full-width" style={{ marginTop: "1rem" }}>
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "left",
              }}
            >
              <div>
                <p className="disclaimer">
                  Disclaimer: The information in this form are intended for
                  University/Office File, Career Placement Service, Alumni
                  Directory, Alumni ID, and Yearbook purposes.
                </p>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{ marginRight: "0.5rem" }}
                />
                <span>
                  {" "}
                  I agree that my info will be saved in the SMU Alumni database.
                </span>
              </div>
              {/* <span>
                I agree to the{' '}
                <span
                  style={{
                    color: '#007BFF',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => window.open('/terms', '_blank')}
                >
                  Terms and Conditions
                </span>{' '}
                regarding data privacy.
                
              </span> */}
            </label>
            {!acceptedTerms && (
              <p className="error-message">
                You must accept the Terms and Conditions to proceed.
              </p>
              // <p>Disclaimer: The information in this form are intended for University/Office File, Career Placement Service, Alumni Directory, and Yearbook purposes.</p>
            )}
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>

        <div className="buttons-container">
          <button type="button" className="prev-button" onClick={prevStep}>
            Previous
          </button>
          <button
            type="submit"
            className="signup-button"
            disabled={!acceptedTerms}
            style={{
              opacity: acceptedTerms ? 1 : 0.6,
              cursor: acceptedTerms ? "pointer" : "not-allowed",
            }}
          >
            Submit
          </button>
        </div>
      </>
    );
  };

  // Render steps indicator
  const renderStepIndicator = () => {
    return (
      <div className="steps-indicator">
        <div className={`step-circle ${step >= 1 ? "active" : ""}`}></div>
        <div className="step-line"></div>
        <div className={`step-circle ${step >= 2 ? "active" : ""}`}></div>
        <div className="step-line"></div>
        <div className={`step-circle ${step >= 3 ? "active" : ""}`}></div>
        <div className="step-line"></div>
        <div className={`step-circle ${step >= 4 ? "active" : ""}`}></div>
      </div>
    );
  };

  return (
    <div className="signup-container">
      <div className="signup-box">
        <img src="/smulogo.png" alt="SMU Logo" className="form-logo" />
        <h1 className="signup-title">REGISTER</h1>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          <p className="signup-text">
            Already have an account?{" "}
            <Link to="/login" className="link">
              Login
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
